/* eslint-disable max-statements */
import { Button, Center, Group, Loader, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useState } from 'react';

import { IUsersCriteria, User } from '@e-origin/shared';

import { deleteIcon, notReleasedIcon, releasedIcon, viewIcon } from '@assets';
import { IconBox, SearchBox, Table, TableActions, TableColumnProps } from '@components';
import { defaultUsersCriteria, useDeclarantUsers, useDeleteUser, useUser } from '@hooks/user';
import { EditUser } from './EditUser';

export const ManageUsers = () => {
  const [filters, setFilters] = useState<IUsersCriteria['filters']>(defaultUsersCriteria.filters);
  const [sorting, setSorting] = useState<IUsersCriteria['sorting']>(defaultUsersCriteria.sorting);
  const [pagination, setPagination] = useState<IUsersCriteria['pagination']>(defaultUsersCriteria.pagination);
  const { data: currentUser } = useUser();

  const { data: { data: { users = [], totalItems = 0 } = {} } = {}, isLoading } = useDeclarantUsers({
    filters,
    sorting,
    pagination,
  });
  const deleteUserMutation = useDeleteUser();

  const openDeleteUser = (user: User) => {
    modals.openConfirmModal({
      title: 'Delete User',
      children: <Text>Are you sure you want to delete this user?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteUserMutation.mutate(user.email);
      },
    });
  };

  const openUserModal = (user?: User) => {
    modals.open({
      title: user ? 'Edit User' : 'New User',
      children: <EditUser user={user} currentUser={currentUser} onHide={() => modals.closeAll()} />,
      size: 'lg',
    });
  };

  const handleSearch = (searchQuery: string) => {
    setFilters((prev) => ({
      ...prev,
      searchQuery,
    }));
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const columns: TableColumnProps<User>[] = [
    {
      name: 'Email',
      cell: (row) => row.email,
      sortable: true,
      selector: 'email',
    },
    {
      name: 'First Name',
      cell: (row) => row.firstName,
      sortable: true,
      selector: 'firstName',
    },
    {
      name: 'Last Name',
      cell: (row) => row.lastName,
      sortable: true,
      selector: 'lastName',
    },
    {
      name: 'Visible',
      cell: (row) => <IconBox icon={row.visible === false ? notReleasedIcon : releasedIcon} />,
      sortable: true,
      selector: 'visible',
    },
    {
      name: 'Active',
      cell: (row) => <IconBox icon={row.deleted ? notReleasedIcon : releasedIcon} />,
      sortable: true,
      selector: 'deleted',
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row) => {
        const actions = [
          {
            icon: viewIcon,
            text: 'View User',
            onClick: () => openUserModal(row),
          },
        ];

        if (currentUser?._id !== row._id) {
          actions.push({
            icon: deleteIcon,
            text: 'Delete',
            onClick: () => openDeleteUser(row),
          });
        }

        return <TableActions actions={actions} />;
      },
    },
  ];

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <Stack>
      <Group justify="flex-end" mb="md">
        <Button onClick={() => openUserModal()} variant="outline" size="lg">
          Add new
        </Button>
        <SearchBox placeholder="Search by email, name, role or deleted" handleSearch={handleSearch} value={filters?.searchQuery} />
      </Group>
      <Table
        columns={columns}
        dataSource={users || []}
        total={totalItems}
        onPaginationChange={handlePagination}
        onSortingChange={handleSorting}
        defaultPage={pagination.page}
        defaultPerPage={pagination.size}
        defaultSorting={sorting.field}
        defaultSortingDirection={sorting.direction}
        verticalSpacing="xs"
      />
    </Stack>
  );
};
